import {
    useEffect,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IHoverCardContentProps from "./type";
import type {
    HoverCardContentKeyboardEventType,
    HoverCardContentWebPropsType
} from "./type";
import stylesheet, {
    getHoverCardContentWidth,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useHoverCardContext
} from "../../context";
import {
    handlePopoverTabKeyDown
} from "../../../popover/util";
import PopoverSurface from "../../../popoverSurface";
import BottomSheet from "../../../bottomSheet";
import Skeleton from "../../../skeleton";
import Button from "../../../button";
import Text from "../../../text";

const HoverCardContent: FC<IHoverCardContentProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    "aria-label": ariaLabel,
    isLoading,
    openTitle,
    children,
    summary,
    media,
    style
}) => {
    const context = useHoverCardContext({
        part: "Content"
    });

    const customLocalize = customLocalizeProp ?? context?.customLocalize;

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        shapeConfigs,
        avatarSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        placeholderStats: placeholderStatsDynamicStyle,
        placeholderHead: placeholderHeadDynamicStyle,
        placeholderText: placeholderTextDynamicStyle,
        sheetFooter: sheetFooterDynamicStyle,
        content: contentDynamicStyle,
        media: mediaDynamicStyle
    } = useStyles({
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    });

    useEffect(() => {
        context?.onSummaryChange(summary);

        return () => {
            context?.onSummaryChange(undefined);
        };
    }, [
        summary
    ]);

    if(!context) {
        return null;
    }

    const {
        handleContentEnter,
        handleContentLeave,
        handleCancelClose,
        handleSheetClose,
        targetPressRef,
        handleClose,
        isSheetOpen,
        surfaceRef,
        anchorRef,
        contentId,
        isEnabled,
        summaryId,
        isOpen,
        align,
        side
    } = context;

    const onKeyDown = (event: HoverCardContentKeyboardEventType) => {
        handlePopoverTabKeyDown({
            triggerRef: anchorRef,
            onLeave: handleClose,
            surfaceRef,
            event
        });
    };

    const handleOpenTarget = () => {
        targetPressRef.current?.();

        handleSheetClose();
    };

    const renderPlaceholder = () => {
        return <View
            style={[
                stylesheet.content,
                contentDynamicStyle
            ]}
        >
            <View
                style={[
                    stylesheet.placeholderHead,
                    placeholderHeadDynamicStyle
                ]}
            >
                <Skeleton
                    size={avatarSizes.medium}
                    customTheme={customTheme}
                    variant="circle"
                />
                <View
                    style={[
                        stylesheet.placeholderText,
                        placeholderTextDynamicStyle
                    ]}
                >
                    <Skeleton
                        typography="titleMediumSize"
                        customTheme={customTheme}
                        variant="text"
                        width="55%"
                    />
                    <Skeleton
                        typography="bodyMediumSize"
                        customTheme={customTheme}
                        variant="text"
                        width="35%"
                    />
                </View>
            </View>
            <Skeleton
                typography="bodyMediumSize"
                customTheme={customTheme}
                variant="text"
                lines={2}
            />
            <View
                style={[
                    stylesheet.placeholderStats,
                    placeholderStatsDynamicStyle
                ]}
            >
                {
                    [
                        "first",
                        "second",
                        "third"
                    ].map((key) => <View
                        style={stylesheet.placeholderStat}
                        key={key}
                    >
                        <Skeleton
                            customTheme={customTheme}
                        />
                    </View>)
                }
            </View>
        </View>;
    };

    const renderBody = () => {
        return <Skeleton.Group
            placeholder={renderPlaceholder()}
            customLocalize={customLocalize}
            customTheme={customTheme}
            isLoading={!!isLoading}
        >
            <View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                {children}
            </View>
        </Skeleton.Group>;
    };

    const renderMedia = () => {
        if(!media) {
            return null;
        }

        return <View
            style={[
                stylesheet.media,
                mediaDynamicStyle
            ]}
        >
            {media}
        </View>;
    };

    const renderSheetFooter = () => {
        return <View
            style={[
                stylesheet.sheetFooter,
                sheetFooterDynamicStyle
            ]}
        >
            <Button
                title={openTitle ?? localize("open")}
                onPress={handleOpenTarget}
                customTheme={customTheme}
                spreadBehaviour="stretch"
                size="large"
            />
        </View>;
    };

    if(Platform.OS !== "web") {
        return <BottomSheet
            renderBottom={renderSheetFooter}
            customLocalize={customLocalize}
            modalProps={{
                "aria-label": ariaLabel
            }}
            onClosed={handleSheetClose}
            customTheme={customTheme}
            isOpen={isSheetOpen}
            isAutoHeight={true}
            isShowClose={false}
        >
            {renderBody()}
        </BottomSheet>;
    }

    const webProps: HoverCardContentWebPropsType = {
        onKeyDown
    };

    return <>
        {
            summary ?
                <Text
                    customTheme={customTheme}
                    style={stylesheet.hidden}
                    id={summaryId}
                >
                    {summary}
                </Text>
            :
                null
        }
        <PopoverSurface
            {...webProps}
            onPointerLeave={handleContentLeave}
            collisionPadding={spaces.spacingMd}
            onPointerEnter={handleContentEnter}
            width={getHoverCardContentWidth({
                shapeConfigs
            })}
            isOpen={isOpen && isEnabled}
            onFocus={handleCancelClose}
            customTheme={customTheme}
            onDismiss={handleClose}
            aria-label={ariaLabel}
            aria-busy={isLoading}
            anchorRef={anchorRef}
            padding="spacingMd"
            ref={surfaceRef}
            id={contentId}
            role="dialog"
            style={style}
            align={align}
            side={side}
        >
            <View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                {renderMedia()}
                {renderBody()}
            </View>
        </PopoverSurface>
    </>;
};
export default HoverCardContent;
